// ===== 作業実施タブ(サブタブ = 1 作業 1 パネル) =====

import { useState, useMemo, useRef } from "react";
import { Stack, Box, Button, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageContext } from "../PageContext";
import { ExtendedDB } from "../../../types/extendedDB";
import type { ProcessExecutionBase } from "../../../schema/generated/ProcessExecution";
import type { ConstructionActionBase } from "../../../schema/generated/ConstructionAction";
import { computeElapsedSec, PauseMap } from "./helpers";
import { SectionCard, StepStatus } from "./SectionCard";
import { TimerControl } from "./TimerControl";
import { PeSelectField } from "./SessionFields";
import { errorMessageOf } from "../../../util/typed";

export function ConstructionActionTab({
  pageContext, db, pes, actions, activeActionId, setActiveActionId, actionStatus,
  pauseMap, setPauseMap, now, ensureConstruction, reload, setSnackbar,
}: {
  pageContext: PageContext; db: ExtendedDB;
  pes: ProcessExecutionBase[]; actions: ConstructionActionBase[];
  activeActionId: string | null; setActiveActionId: (v: string | null) => void;
  actionStatus: StepStatus;
  pauseMap: PauseMap; setPauseMap: (updater: (prev: PauseMap) => PauseMap) => void;
  now: number;
  ensureConstruction: (planOverride?: string) => Promise<string>;
  reload: () => Promise<unknown> | void;
  setSnackbar: (s: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const activeAction = useMemo(() => {
    if (activeActionId == null || activeActionId === "new") {
      return null;
    }
    return actions.find((a) => a.id === activeActionId) ?? null;
  }, [actions, activeActionId]);

  const run = async (fn: () => Promise<void>, errLabel: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      pageContext.api.showError(`${errLabel}: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  // 作業の新規作成: 工程実施を選んだ時点で行を作る(タイマー未開始)。施工箇所は工程実施が保持する。
  const createAction = (peId: string) => run(async () => {
    const pe = pes.find((p) => p.id === peId);
    if (!pe) {
      throw new Error("選択した工程実施が見つかりません。再読み込みしてください。");
    }
    const cid = await ensureConstruction();
    const nextOrder = actions.length > 0 ? Math.max(...actions.map((r) => r.sort_order)) + 1 : 1;
    const res = await pageContext.api.entities.putRow("constructionActions", {
      construction_id: cid,
      sort_order: nextOrder,
      process_execution_id: pe.id,
      work_detail_id: pe.work_detail_id,
      started_at: null,
      finished_at: null,
      duration_seconds: null,
      actual_qty: null,
      remarks: null,
    });
    setSnackbar("作業実施を作成しました");
    await reload();
    if (res?.id != null) {
      setActiveActionId(String(res.id));
    }
  }, "作成に失敗しました");

  const patchAction = (row: ConstructionActionBase, changes: Partial<Pick<ConstructionActionBase, "process_execution_id" | "work_detail_id" | "started_at" | "finished_at" | "duration_seconds" | "actual_qty" | "remarks">>) =>
    pageContext.api.entities.patchRow("constructionActions", {
      id: row.id,
      construction_id: row.construction_id,
      sort_order: row.sort_order,
      process_execution_id: row.process_execution_id,
      work_detail_id: row.work_detail_id,
      started_at: row.started_at,
      finished_at: row.finished_at,
      duration_seconds: row.duration_seconds,
      actual_qty: row.actual_qty,
      remarks: row.remarks,
      ...changes,
    });

  const changePe = (row: ConstructionActionBase, peId: string) => run(async () => {
    const pe = pes.find((p) => p.id === peId);
    if (!pe) {
      throw new Error("選択した工程実施が見つかりません。");
    }
    await patchAction(row, { process_execution_id: pe.id, work_detail_id: pe.work_detail_id });
    await reload();
  }, "更新に失敗しました");

  const startTimer = (row: ConstructionActionBase) => run(async () => {
    await patchAction(row, { started_at: Date.now() });
    await reload();
  }, "開始に失敗しました");

  // 一時停止時は duration_seconds に経過秒数を patch する(チェックポイント保存)。
  // リロードで pauseMap(クライアントのみ)が揮発しても、再開ロード側でこの値から paused_total_ms を逆算できる。
  const pauseRow = (row: ConstructionActionBase) => run(async () => {
    const elapsed = computeElapsedSec(row.started_at, null, pauseMap[row.id], Date.now());
    setPauseMap((prev) => {
      const cur = prev[row.id];
      return { ...prev, [row.id]: { pausedTotalMs: cur != null ? cur.pausedTotalMs : 0, pausedAt: Date.now() } };
    });
    await patchAction(row, { duration_seconds: elapsed });
    await reload();
  }, "一時停止に失敗しました");
  const resumeRow = (id: string) => {
    setPauseMap((prev) => {
      const cur = prev[id];
      const add = cur != null && cur.pausedAt != null ? Date.now() - cur.pausedAt : 0;
      return { ...prev, [id]: { pausedTotalMs: (cur != null ? cur.pausedTotalMs : 0) + add, pausedAt: null } };
    });
  };

  const finishTimer = (row: ConstructionActionBase) => run(async () => {
    const elapsed = computeElapsedSec(row.started_at, null, pauseMap[row.id], Date.now());
    await patchAction(row, { finished_at: Date.now(), duration_seconds: elapsed });
    setPauseMap((prev) => {
      const n = { ...prev };
      delete n[row.id];
      return n;
    });
    setSnackbar("作業実施を完了しました");
    await reload();
  }, "完了に失敗しました");

  const saveQtyAndRemarks = (row: ConstructionActionBase, actualQty: string, remarks: string) => run(async () => {
    await patchAction(row, {
      actual_qty: actualQty === "" ? null : Number(actualQty),
      remarks: remarks === "" ? null : remarks,
    });
    await reload();
  }, "保存に失敗しました");

  const del = (id: string) => run(async () => {
    await pageContext.api.entities.setDelete("constructionActions", id, true);
    setSnackbar("作業実施を削除しました");
    await reload();
  }, "削除に失敗しました");

  if (activeAction == null && activeActionId !== "new") {
    return (
      <SectionCard title="作業実施" status={actionStatus}
        errors={pes.length === 0 ? ["先に「施工箇所選択」で施工箇所と工程を登録してください"] : ["作業実施を作成してください"]}
        info="塗る・打つなどの施工作業を記録します。「作業を作成」から始めてください。">
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Button variant="contained" size="large" startIcon={<AddIcon />} disabled={pes.length === 0}
            onClick={() => setActiveActionId("new")}>作業を作成</Button>
        </Box>
      </SectionCard>
    );
  }

  if (activeAction == null) {
    return (
      <SectionCard title="作業選択" status="incomplete"
        errors={["工程を選択してください"]}
        info="施工する工程を選ぶと作業実施が作成されます。">
        <Stack gap={1.5} alignItems="flex-start">
          <PeSelectField db={db} pes={pes} value="" busy={busy} onSelect={(peId) => createAction(peId)} />
          <Button size="small" onClick={() => setActiveActionId(null)}>キャンセル</Button>
        </Stack>
      </SectionCard>
    );
  }

  const selectedPe = pes.find((p) => p.id === activeAction.process_execution_id) ?? null;
  const selectedArea = selectedPe != null ? db.constructionPlanAreas.find((a) => a.id === selectedPe.construction_plan_area_id) : null;
  const actionWd = activeAction.work_detail_id != null ? db.workDetails.find((w) => w.id === activeAction.work_detail_id) : null;
  let qtyUnit = "";
  if (actionWd?.work_subtype_id != null) {
    qtyUnit = db.workSubtypes.find((s) => s.id === actionWd.work_subtype_id)?.unit ?? "";
  }
  const plannedQty = selectedArea?.planned_qty ?? null;
  const timerDone = activeAction.finished_at != null;
  let timerStatus: StepStatus = "incomplete";
  if (timerDone) {
    timerStatus = "done";
  } else if (activeAction.started_at != null) {
    timerStatus = "in_progress";
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={() => del(activeAction.id)}>この作業実施を削除</Button>
      </Stack>

      <SectionCard title="作業選択" status={activeAction.process_execution_id != null ? "done" : "incomplete"}
        errors={activeAction.process_execution_id != null ? [] : ["工程を選択してください"]}
        info="施工する工程を選びます。">
        <PeSelectField db={db} pes={pes} value={activeAction.process_execution_id ?? ""} busy={busy}
          onSelect={(peId) => changePe(activeAction, peId)} />
      </SectionCard>

      <SectionCard title="施工タイマー" status={timerStatus}
        errors={activeAction.process_execution_id == null ? [] : (timerDone ? [] : ["施工タイマーを開始して完了してください"])}
        info={activeAction.process_execution_id == null
          ? "施工タイマーを使用する前に、工程を選択してください。"
          : "施工タイマーで開始〜完了を計測します。攪拌・作業のタイマーは並行して動かせます(施工中に次の攪拌を進められます)。"}>
        {activeAction.process_execution_id != null && (
          <TimerControl
            startedAt={activeAction.started_at} finishedAt={activeAction.finished_at} durationSeconds={activeAction.duration_seconds}
            pause={pauseMap[activeAction.id]} now={now} busy={busy}
            onStart={() => startTimer(activeAction)}
            onPause={() => pauseRow(activeAction)} onResume={() => resumeRow(activeAction.id)} onFinish={() => finishTimer(activeAction)}
          />
        )}
      </SectionCard>

      <SectionCard title="完了数量入力" status={activeAction.actual_qty != null ? "done" : "in_progress"}
        info={activeAction.process_execution_id == null
          ? "完了数量を入力する前に、工程を選択してください。"
          : "施工した数量を記録します(任意・記録用)。"}>
        {activeAction.process_execution_id != null && (
          <ActionQtyFields key={activeAction.id}
            initialQty={activeAction.actual_qty == null ? "" : String(activeAction.actual_qty)}
            initialRemarks={activeAction.remarks ?? ""}
            qtyUnit={qtyUnit} plannedQty={plannedQty} busy={busy}
            onSave={(qty, remarks) => saveQtyAndRemarks(activeAction, qty, remarks)} />
        )}
      </SectionCard>
    </Stack>
  );
}

// 完了数量と備考のローカル編集 + blur 保存。
function ActionQtyFields({ initialQty, initialRemarks, qtyUnit, plannedQty, busy, onSave }: {
  initialQty: string; initialRemarks: string; qtyUnit: string; plannedQty: number | null; busy: boolean;
  onSave: (qty: string, remarks: string) => void;
}) {
  const [qty, setQty] = useState(initialQty);
  const [remarks, setRemarks] = useState(initialRemarks);
  const dirty = qty !== initialQty || remarks !== initialRemarks;
  let helper = "";
  if (plannedQty != null) {
    helper = `計画数量: ${plannedQty}${qtyUnit !== "" ? ` ${qtyUnit}` : ""}`;
  }
  return (
    <Stack gap={1.5}>
      <TextField type="number" size="small" label={qtyUnit !== "" ? `施工完了数量 (${qtyUnit})` : "施工完了数量"} sx={{ width: 220 }}
        value={qty} helperText={helper} disabled={busy}
        onChange={(e) => setQty(e.target.value)}
        onBlur={() => { if (dirty) { onSave(qty, remarks); } }} />
      <TextField size="small" label="備考" value={remarks} multiline minRows={1} disabled={busy}
        onChange={(e) => setRemarks(e.target.value)}
        onBlur={() => { if (dirty) { onSave(qty, remarks); } }} />
    </Stack>
  );
}
